import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { useHistory, useLocation } from 'react-router-dom';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import { last_page_offset } from '../shared/paging';
import { useHeadingRefocus } from '../shared/use_heading_refocus';
import { fetch_close_versions_csv, fetch_close_versions_list, post_recalc } from '../api';
import { api_error, fmt_count } from '../format';
import { RECALC_DONE, RECALC_FAILED, recalc_failure, recalc_outcome } from '../reports/adapters';
import { recalc_payload } from '../additional_payments/upload/requests';
import VersionsDialog from './versions_dialog';
import VersionsTable, { SortSelect } from './versions_table';
import { cv_error_view, empty_state, list_view, run_banner_text, summary_view } from './adapters';
import {
  MODE_CLOSE_VERSIONS,
  MODE_REOPENED_NOW,
  PAGE_SIZE,
  QUERY_MAX_LEN,
  QUERY_TOO_LONG_TEXT,
  clear_filters,
  effective_mode,
  has_filters,
  multi_version_on,
  parse_state,
  state_search,
  with_filter,
  with_mode,
  with_sort
} from './params';
import { csv_fallback_name } from './requests';

/*
 * PROD-7828 D111 — the Close versions tab (/teacher-payments/close-versions). Two modes: "Reopened now"
 * (the reopened version vs the live figures) and "Close versions" (previous close vs latest). The view
 * state lives in the URL, so Overview's "Versions" link and a reload land on the same view.
 * props: pay_month (YYYY-MM; the shell remounts the tab per month), month_label, summary (the shell's
 * use_summary.js read, shared with the tab badge), on_error(e, fallback), on_changed() after a
 * recalculation (the shell re-reads its badges and that summary), on_open_needs_attention().
 */
const LOAD_FAILED_TEXT = 'Could not load close versions. Try again.';
const SEARCH_DEBOUNCE_MS = 300;

const CloseVersionsTab = ({
  pay_month,
  month_label,
  summary: summary_read,
  on_error,
  on_changed,
  on_open_needs_attention
}) => {
  const history = useHistory();
  const location = useLocation();
  const [state, setState] = useState(() => parse_state(location.search, pay_month));
  const [q_draft, setQDraft] = useState(state.q);
  const [list, setList] = useState(null);
  const [list_status, setListStatus] = useState('idle'); // idle | loading | ready | error
  // A re-read (sort, filter, page, recalculation) keeps the rows mounted, so focus stays where it was.
  const [list_busy, setListBusy] = useState(false);
  const [list_error, setListError] = useState('');
  const [list_rev, setListRev] = useState(0);
  const [recalc, setRecalc] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [csv_error, setCsvError] = useState('');
  const list_seq = useRef(0);
  const has_list = useRef(false);
  has_list.current = list !== null;
  const heading_ref = useRef(null);
  const on_focus = useHeadingRefocus(heading_ref);

  const summary = useMemo(
    () => (summary_read.data ? summary_view(summary_read.data) : null),
    [summary_read.data]
  );
  const mode = effective_mode(state, summary);
  // The server's default is taken once: a later summary re-read (after a recalculation) never flips the mode.
  useEffect(() => {
    if (mode && !state.mode) setState((s) => ({ ...s, mode }));
  }, [mode, state.mode]);

  // Returns the inline text, or '' once a permission failure was handed to the shell.
  const inline_error = useCallback(
    (e, fallback) => {
      const v = cv_error_view(e, fallback);
      if (v.kind === 'permission') {
        on_error(e, fallback);
        return '';
      }
      return v.text;
    },
    [on_error]
  );

  // A 403 on the shared summary sends the shell to its no-permission page; other failures show here.
  const summary_failure = summary_read.status === 'error' ? summary_read.error : null;
  const summary_error = summary_failure ? cv_error_view(summary_failure, LOAD_FAILED_TEXT) : null;
  useEffect(() => {
    if (summary_failure && cv_error_view(summary_failure, '').kind === 'permission') {
      on_error(summary_failure, LOAD_FAILED_TEXT);
    }
  }, [summary_failure, on_error]);

  // The list waits for a mode: the URL's, else the summary's default_mode.
  const { only_changed, multi_version, q, sort, dir, offset } = state;
  useEffect(() => {
    if (!mode) return;
    list_seq.current += 1;
    const mine = list_seq.current;
    if (has_list.current) setListBusy(true);
    else setListStatus('loading');
    setListError('');
    const request = { only_changed, multi_version, q, sort, dir, offset };
    fetch_close_versions_list(pay_month, request, mode)
      .then((data) => {
        if (mine !== list_seq.current) return;
        setList(list_view(data, mode));
        setListStatus('ready');
        setListBusy(false);
      })
      .catch((e) => {
        if (mine !== list_seq.current) return;
        setList(null);
        setListBusy(false);
        setListError(inline_error(e, LOAD_FAILED_TEXT));
        setListStatus('error');
      });
  }, [pay_month, mode, only_changed, multi_version, q, sort, dir, offset, list_rev, inline_error]);

  // The URL mirrors the view (replace, not push: filters are not history steps), merged into the
  // current search so the shell's own ?month= (or any other param) is never clobbered.
  useEffect(() => {
    const next_qs = state_search(location.search, state, pay_month);
    if (location.search.replace(/^\?/, '') !== next_qs) {
      history.replace({ pathname: location.pathname, search: next_qs ? `?${next_qs}` : '' });
    }
  }, [state, pay_month, history, location.pathname, location.search]);

  // The search box sends after a pause; a too-long query is refused here (the server's BAD_QUERY).
  const q_too_long = q_draft.trim().length > QUERY_MAX_LEN;
  useEffect(() => {
    if (q_too_long || q_draft.trim() === q) return undefined;
    const t = setTimeout(
      () => setState((s) => with_filter(s, { q: q_draft.trim() })),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [q_draft, q, q_too_long]);

  const empty =
    list_status === 'ready' && list
      ? empty_state({
          mode,
          list,
          filters_on: state.only_changed || state.q !== '',
          only_multi: multi_version_on(state, mode),
          month_label
        })
      : null;

  // An empty page past the end (a recalculation shrank the list) steps back to the last page with rows.
  useEffect(() => {
    if (!empty || empty.action !== 'page_back') return;
    const back = last_page_offset({
      offset: state.offset,
      row_count: 0,
      total_count: list.filtered_count,
      page_size: PAGE_SIZE
    });
    if (back !== null) setState((s) => ({ ...s, offset: back }));
  }, [empty, list, state.offset]);

  const set_mode = (m) => {
    if (m === mode) return;
    setRecalc({});
    // Another mode has other columns: its first read shows the spinner, not this mode's rows.
    setList(null);
    setState((s) => with_mode(s, m));
  };
  const reset_filters = () => {
    setQDraft('');
    setState(clear_filters);
  };

  const open_teacher = useCallback(
    (row) => setState((s) => ({ ...s, teacher_uuid: row.teacher_uuid })),
    []
  );
  // A dialog opened by a link (Overview's "Versions") has no opener to hand focus back to.
  const focus_heading = useCallback(() => {
    if (heading_ref.current) heading_ref.current.focus({ preventScroll: true });
  }, []);
  const close_dialog = useCallback(() => setState((s) => ({ ...s, teacher_uuid: '' })), []);

  // The list re-reads here; on_changed has the shell re-read the summary and the badges.
  const refresh_after_recalc = () => {
    setListRev((r) => r + 1);
    if (on_changed) on_changed();
  };

  // C8: the existing audited recompute (POST /recalc), one teacher; the row refreshes after it.
  const run_recalc = (row) => {
    const uuid = row.teacher_uuid;
    if (recalc[uuid] && recalc[uuid].busy) return;
    const set_row = (v) => setRecalc((r) => ({ ...r, [uuid]: v }));
    set_row({ busy: true, text: '', failed: false });
    post_recalc(recalc_payload(uuid, pay_month))
      .then((data) => {
        const outcome = recalc_outcome(data);
        const done = outcome.kind === RECALC_DONE;
        set_row({ busy: false, text: outcome.text, failed: outcome.kind === RECALC_FAILED });
        if (done) refresh_after_recalc();
      })
      .catch((e) => {
        if (cv_error_view(e, '').kind === 'permission') {
          set_row({ busy: false, text: '', failed: false });
          on_error(e, 'You do not have access to recalculate teacher payments.');
          return;
        }
        set_row({ busy: false, text: recalc_failure(api_error(e).message).text, failed: true });
      });
  };

  const download_csv = () => {
    if (downloading || !mode) return;
    setDownloading(true);
    setCsvError('');
    fetch_close_versions_csv(pay_month, state, mode)
      .then(({ csv, file_name }) => {
        if (!csv) {
          setCsvError('The CSV export came back empty. Try again.');
        } else {
          saveAs(
            new Blob([csv], { type: 'text/csv;charset=utf-8' }),
            file_name || csv_fallback_name(mode, pay_month)
          );
        }
        setDownloading(false);
      })
      .catch((e) => {
        setCsvError(inline_error(e, 'Could not download the close versions CSV.'));
        setDownloading(false);
      });
  };

  const summary_line = () => {
    if (!summary) return '';
    if (mode === MODE_REOPENED_NOW) {
      return `${fmt_count(summary.reopened_now)} reopened · ${fmt_count(
        summary.reopened_pending_recompute
      )} not recalculated since the reopen`;
    }
    return `${fmt_count(summary.closed_teacher_months)} closed · ${fmt_count(
      summary.multi_version
    )} closed more than once · ${fmt_count(summary.changed)} changed at the latest close`;
  };

  const banner = summary ? run_banner_text(summary.active_run, month_label) : '';
  const rows = list ? list.rows : [];
  const selected = rows.find((r) => r.teacher_uuid === state.teacher_uuid);
  const page_start = list && list.filtered_count ? state.offset + 1 : 0;
  const page_end = list ? Math.min(state.offset + rows.length, list.filtered_count) : 0;
  const prev_off = state.offset <= 0;
  const next_off = !list || state.offset + PAGE_SIZE >= list.filtered_count;
  const csv_off = downloading || !mode;

  return (
    <div className="tp-cv" onFocus={on_focus}>
      <div className="tp-card-hd">
        <span
          className="tp-card-hd-title"
          role="heading"
          aria-level={2}
          tabIndex={-1}
          ref={heading_ref}
        >
          Close versions
        </span>
        <span className="tp-card-hd-sub">{summary_line()}</span>
      </div>

      {banner ? (
        <div className="tp-banner tp-banner-info">
          {banner}{' '}
          <button type="button" className="tp-link" onClick={on_open_needs_attention}>
            Open Needs attention
          </button>
        </div>
      ) : null}

      {summary_read.status === 'loading' && !mode ? <CustomLoading /> : null}
      {/* Without a mode there is no list to show; with one, a failed summary only loses the counts. */}
      {summary_error && summary_error.kind === 'inline' && !mode ? (
        <LoadFailed text={summary_error.text} on_retry={summary_read.reload} />
      ) : null}

      {mode ? (
        <>
          <div className="tp-seg" role="group" aria-label="Compare">
            <button
              type="button"
              className={mode === MODE_REOPENED_NOW ? 'on' : ''}
              aria-pressed={mode === MODE_REOPENED_NOW}
              onClick={() => set_mode(MODE_REOPENED_NOW)}
            >
              Reopened now{summary ? ` (${fmt_count(summary.reopened_now)})` : ''}
            </button>
            <button
              type="button"
              className={mode === MODE_CLOSE_VERSIONS ? 'on' : ''}
              aria-pressed={mode === MODE_CLOSE_VERSIONS}
              onClick={() => set_mode(MODE_CLOSE_VERSIONS)}
            >
              Close versions
            </button>
          </div>

          <div className="tp-frow">
            <label className="tp-cv-check">
              <input
                type="checkbox"
                checked={state.only_changed}
                onChange={(e) =>
                  setState((s) => with_filter(s, { only_changed: e.target.checked }))
                }
              />
              Only changed
            </label>
            {mode === MODE_CLOSE_VERSIONS ? (
              <label className="tp-cv-check">
                <input
                  type="checkbox"
                  checked={multi_version_on(state, mode)}
                  onChange={(e) =>
                    setState((s) => with_filter(s, { multi_version: e.target.checked }))
                  }
                />
                More than one version
              </label>
            ) : null}
            <div className="flex flex-col gap-1">
              <input
                className="tp-input"
                type="search"
                value={q_draft}
                onChange={(e) => setQDraft(e.target.value)}
                placeholder="Search by teacher name or email"
                aria-label="Search teachers by name or email"
                aria-invalid={q_too_long ? 'true' : undefined}
                aria-describedby={q_too_long ? 'tp-cv-q-error' : undefined}
              />
              {q_too_long ? (
                <span id="tp-cv-q-error" className="tp-neg" role="alert">
                  {QUERY_TOO_LONG_TEXT}
                </span>
              ) : null}
            </div>
            {has_filters(state, mode) ? (
              <button type="button" className="tp-link" onClick={reset_filters}>
                Clear filters
              </button>
            ) : null}
            <div className="tp-frow-right">
              <button
                type="button"
                className="tp-btn-out"
                onClick={download_csv}
                aria-disabled={csv_off ? 'true' : undefined}
              >
                {downloading ? 'Preparing…' : 'Download CSV'}
              </button>
            </div>
          </div>

          {csv_error ? (
            <div className="tp-banner tp-banner-error" role="alert">
              {csv_error}
            </div>
          ) : null}

          {list_status === 'loading' ? <CustomLoading /> : null}
          {list_status === 'error' && list_error ? (
            <LoadFailed text={list_error} on_retry={() => setListRev((r) => r + 1)} />
          ) : null}

          {list_status === 'ready' && empty && empty.text ? (
            <div className="tp-empty">
              {empty.text}{' '}
              {empty.action === 'show_close_versions' ? (
                <button
                  type="button"
                  className="tp-link"
                  onClick={() => set_mode(MODE_CLOSE_VERSIONS)}
                >
                  Show close versions
                </button>
              ) : null}
              {empty.action === 'show_first_closes' ? (
                <button
                  type="button"
                  className="tp-link"
                  onClick={() => setState((s) => with_filter(s, { multi_version: false }))}
                >
                  Show first closes too
                </button>
              ) : null}
              {empty.action === 'clear_filters' ? (
                <button type="button" className="tp-link" onClick={reset_filters}>
                  Clear filters
                </button>
              ) : null}
            </div>
          ) : null}

          {list_status === 'ready' && rows.length ? (
            <>
              <div className="tp-cv-list" aria-busy={list_busy ? 'true' : undefined}>
                <SortSelect
                  sort={{ key: state.sort, dir: state.dir }}
                  on_change={(key, d) => setState((s) => ({ ...s, sort: key, dir: d, offset: 0 }))}
                />
                <VersionsTable
                  mode={mode}
                  rows={rows}
                  sort={{ key: state.sort, dir: state.dir }}
                  on_sort={(key) => setState((s) => with_sort(s, key))}
                  on_open={open_teacher}
                  recalc={recalc}
                  on_recalc={run_recalc}
                />
              </div>
              <div className="tp-frow" style={{ marginTop: 12 }}>
                <span className="tp-card-hd-sub">
                  Showing {fmt_count(page_start)}–{fmt_count(page_end)} of{' '}
                  {fmt_count(list.filtered_count)}
                </span>
                <div className="tp-frow-right">
                  <button
                    type="button"
                    className="tp-btn-out"
                    aria-disabled={prev_off ? 'true' : undefined}
                    onClick={() =>
                      prev_off
                        ? undefined
                        : setState((s) => ({ ...s, offset: Math.max(0, s.offset - PAGE_SIZE) }))
                    }
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="tp-btn-out"
                    aria-disabled={next_off ? 'true' : undefined}
                    onClick={() =>
                      next_off
                        ? undefined
                        : setState((s) => ({ ...s, offset: s.offset + PAGE_SIZE }))
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {state.teacher_uuid ? (
        <VersionsDialog
          pay_month={pay_month}
          month_label={month_label}
          teacher_uuid={state.teacher_uuid}
          teacher_name={selected ? selected.teacher_name : ''}
          on_close={close_dialog}
          on_focus_lost={focus_heading}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
};

export default CloseVersionsTab;
